import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Autocomplete.css';

interface AutocompleteInputProps {
  id: string;
  value: string;
  options: string[];
  selectedValues?: string[];
  placeholder?: string;
  onChange: (value: string) => void;
  onSelectOption: (value: string) => void;
  inputClassName?: string;
  wrapperClassName?: string;
}

const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  id,
  value,
  options,
  selectedValues = [],
  placeholder,
  onChange,
  onSelectOption,
  inputClassName,
  wrapperClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(() => {
    const normalized = value.trim().toLowerCase();
    const available = options.filter((opt) => !selectedValues.includes(opt));
    if (!normalized) return available.slice(0, 200);
    return available.filter((opt) => opt.toLowerCase().includes(normalized)).slice(0, 200);
  }, [options, selectedValues, value]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (document.activeElement === inputRef.current && filteredOptions.length > 0) {
      setIsOpen(true);
    }
  }, [filteredOptions.length]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setIsOpen(true);
      setHighlightedIndex(0);
      return;
    }
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = highlightedIndex + 1;
        setHighlightedIndex(next >= filteredOptions.length ? 0 : next);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = highlightedIndex - 1;
        setHighlightedIndex(prev < 0 ? filteredOptions.length - 1 : prev);
        break;
      }
      case 'Enter': {
        if (isOpen && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          e.preventDefault();
          onSelectOption(filteredOptions[highlightedIndex]);
          setIsOpen(false);
          setHighlightedIndex(-1);
        }
        break;
      }
      case 'Escape': {
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      }
    }
  };

  return (
    <div className={`ac-wrapper${wrapperClassName ? ' ' + wrapperClassName : ''}`} ref={wrapperRef}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={() => {
          if (filteredOptions.length > 0) setIsOpen(true);
        }}
        onClick={() => {
          if (filteredOptions.length > 0) setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        className={inputClassName ? inputClassName : 'ac-input'}
      />
      {isOpen && filteredOptions.length > 0 && (
        <ul className="ac-list" role="listbox" aria-labelledby={id}>
          {filteredOptions.map((opt, idx) => (
            <li
              key={`${opt}-${idx}`}
              role="option"
              aria-selected={idx === highlightedIndex}
              className={`ac-item ${idx === highlightedIndex ? 'active' : ''}`}
              onMouseEnter={() => setHighlightedIndex(idx)}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onClick={() => {
                onSelectOption(opt);
                setIsOpen(false);
                setHighlightedIndex(-1);
                inputRef.current?.focus();
              }}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AutocompleteInput;


